/* import __COLOCATED_TEMPLATE__ from './edit-company-credit.hbs'; */
import Component from '@glimmer/component';
import clsx from 'clsx';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { all, dropTask, task, timeout } from 'ember-concurrency';

const CLASS_NAME = 'SH-c-edit-company-credit';

export default class EditCompanyCredit extends Component
{
  @service('state-managers/application') applicationStateManager;
  @service('commands/company-credit-commands') companyCreditCommandService;
  @service('commands/sh-general-invoice-commands') shGeneralInvoiceCommandService;
  @service('commands/sh-general-invoice-transaction-commands') shGeneralInvoiceTransactionCommandService;

  /** @var CompanyCredit */
  @tracked companyCredit = null;

  @tracked _showAddCompanyCredit;
  @tracked _showUsedCredits;
  @tracked blinkShowAddCompanyCredit = false;

  @tracked companyCreditFactoids = [];

  @tracked costCodes = [];
  @tracked costCodesCurrentPage = null;

  @tracked shGeneralPaidInvoices = [];
  @tracked shGeneralPaidInvoicesPagination = null;

  @tracked shGeneralUnpaidInvoices = [];
  @tracked shGeneralUnpaidInvoicesPagination = null;

  @tracked shGeneralDeletedInvoices = [];
  @tracked shGeneralDeletedInvoicesPagination = null;

  get baseClassName() {
    return CLASS_NAME;
  }

  get classNames() {
    return clsx(
      this.args.class,
      this.baseClassName
    );
  }

  get showAddCompanyCreditClassNames() {
    const classes = [
      'SH-r-reports-company-credits',
      `${this.baseClassName}__company-credit-list-item`,
      `${this.baseClassName}__add-company-credit`,
    ];

    if (this.blinkShowAddCompanyCredit) {
      classes.push(`${this.baseClassName}__blink`)
    }
    return clsx(classes);
  }

  get showReport() {
    return this.companyCredits.length || this.args.loadCompanyCostCodeCreditsTask.isRunning;
  }

  get disabled() {
    return this.deleteCompanyCreditTask.isRunning
      || this.args.loadCompanyCostCodeCreditsTask.isRunning
      || this.saveOrUpdateCompanyCreditTask.isRunning
      || this.saveShGeneralInvoiceTransactionTask.isRunning
      || this.deleteShGeneralInvoiceTransactionTask.isRunning;
  }

  get showAddCompanyCredit() {
    if (this._showAddCompanyCredit === true || this._showAddCompanyCredit === false) {
      return this._showAddCompanyCredit;
    }

    if (!this.args.loadCompanyCostCodeCreditsTask.isRunning && !this.companyCredits.length) {
      return true;
    }

    return false;
  }

  @action
  onAddCompanyCredit() {
    if (this.showAddCompanyCredit) {
      this.blinkAddCompanyCreditTask.perform();
    }

    this._showAddCompanyCredit = true;
  }

  @action
  onCancelEdit(companyCredit) {
    if (!companyCredit) {
      this._showAddCompanyCredit = false;
    }
  }

  @action
  toggleShowUsedCredits() {
    this._showUsedCredits = !this._showUsedCredits;
  }

  @action
  updateCompanyCostCodeCredits(costCodes, page = 1) {
    this.costCodes = costCodes;
    this.costCodesCurrentPage = page;
  }

  @action
  updateCompanyCreditFactoids(factoids) {
    this.companyCreditFactoids = factoids;
  }


  @action
  updateShGeneralPaidInvoices(shGeneralInvoices, pagination) {
    this.shGeneralPaidInvoices = shGeneralInvoices;
    this.shGeneralPaidInvoicesPagination = pagination;
  }

  @action
  updateShGeneralUnpaidInvoices(shGeneralInvoices, pagination) {
    this.shGeneralUnpaidInvoices = shGeneralInvoices;
    this.shGeneralUnpaidInvoicesPagination = pagination;
  }

  @action
  updateShGeneralDeletedInvoices(shGeneralInvoices, pagination) {
    this.shGeneralDeletedInvoices = shGeneralInvoices;
    this.shGeneralDeletedInvoicesPagination = pagination;
  }

  @dropTask
  *blinkAddCompanyCreditTask() {
    this.blinkShowAddCompanyCredit = true;
    yield timeout(750);
    this.blinkShowAddCompanyCredit = false;
  }

  @dropTask
  *deleteCompanyCreditTask(companyCredit) {
    try {
      yield this.companyCreditCommandService.destroyCompanyCredit(companyCredit.id);
    } catch (e) {
      this.applicationStateManager.addToast('Failed to Delete');
      throw e;
    }

    this.applicationStateManager.addToast('Deleted');

    yield this.args.loadCompanyCostCodeCreditsTask.perform();
  }

  @dropTask
  *saveOrUpdateCompanyCreditTask({
    companyCredit = null,
    amountInCents,
    costCodeId,
    memo,
  }) {
    if (companyCredit) {
      try {
        yield this.companyCreditCommandService.updateCompanyCredit({
          companyCreditId: companyCredit.id,
          amountInCents,
          costCodeId,
          memo
        });
      } catch (e) {
        this.applicationStateManager.addToast('Failed to Save');
        throw e;
      }
    } else {
      try {
        yield this.companyCreditCommandService.storeCompanyCredit({
          companyId: this.args.company.id,
          amountInCents,
          costCodeId,
          memo
        });
      } catch (e) {
        this.applicationStateManager.addToast('Failed to Save');
        throw e;
      }
    }

    this._showAddCompanyCredit = false;
    this.applicationStateManager.addToast('Saved');

    yield this.args.loadCompanyCostCodeCreditsTask.perform();
  }

  @dropTask
  *deleteShGeneralInvoiceTransactionTask({
    shGeneralInvoiceTransaction,
  }) {
    try {
      yield this.shGeneralInvoiceTransactionCommandService.deleteShGeneralInvoiceTransactionTask({
        shGeneralInvoiceTransactionId: shGeneralInvoiceTransaction.id,
      });
    } catch (e) {
      this.applicationStateManager.addToast('Failed to Delete');
      throw e;
    }

    this.applicationStateManager.addToast('Removed');

    yield this.args.loadCompanyCostCodeCreditsTask.perform();
  }

  @dropTask
  *saveShGeneralInvoiceTransactionTask({
    shGeneralInvoice,
    amountInCents,
    paymentMethod,
    transactionDate,
    memo,
  }) {
    try {
      yield this.shGeneralInvoiceCommandService.storeShGeneralInvoiceTransaction({
        shGeneralInvoiceId: shGeneralInvoice.id,
        amountInCents,
        paymentMethod,
        transactionDate,
        memo,
      });
    } catch (e) {
      this.applicationStateManager.addToast('Failed to Save');
      throw e;
    }

    this.applicationStateManager.addToast('Saved');

    yield this.args.loadCompanyCostCodeCreditsTask.perform();
  }

  @task
  *initializeModel() {
    const [
      companyCreditFactoids,
      companyCostCodeCredits,
      creditUnpaidInvoices,
      creditPaidInvoices,
      creditDeletedInvoices,
    ] = yield all([
      this.args.loadCompanyCreditFactoidsTask.perform(),
      this.args.loadCompanyCostCodeCreditsTask.perform(),
      this.args.loadCreditUnpaidInvoicesTask.perform(),
      this.args.loadCreditPaidInvoicesTask.perform(),
      this.args.loadCreditDeletedInvoicesTask.perform(),
    ]);

    this.companyCreditFactoids = companyCreditFactoids;

    this.costCodes = companyCostCodeCredits?.costCodes ?? [];
    this.costCodesCurrentPage = companyCostCodeCredits?.costCodesCurrentPage;

    this.shGeneralUnpaidInvoices = creditUnpaidInvoices?.shGeneralUnpaidInvoices ?? [];
    this.shGeneralUnpaidInvoicesPagination = creditUnpaidInvoices?.shGeneralUnpaidInvoicesPagination;

    this.shGeneralPaidInvoices = creditPaidInvoices?.shGeneralPaidInvoices ?? [];
    this.shGeneralPaidInvoicesPagination = creditPaidInvoices?.shGeneralPaidInvoicesPagination;

    this.shGeneralDeletedInvoices = creditDeletedInvoices?.shGeneralDeletedInvoices ?? [];
    this.shGeneralDeletedInvoicesPagination = creditDeletedInvoices?.shGeneralDeletedInvoicesPagination;
  }
}
